import type { NumberedFeatureCard } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards.types';
import type { NumberedStatItem, NumberedStatRow } from 'ui/organisms/NumberedStatsSection/NumberedStatsSection.types';
import type { AccessRoute } from 'ui/organisms/AccessRoutes/AccessRoutes.types';

type Translate = (key: string) => string;

const LEAKAGE_GAUGE_ICON = <path d="M12 3a9 9 0 1 0 9 9M12 3v4M21 12h-4M12 12l4.5-4.5" />;
const LEAKAGE_TAG_ICON = (
  <>
    <path d="M12 3 3 12l9 9 9-9-9-9z" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
  </>
);
const LEAKAGE_LINK_ICON = (
  <path d="M10.5 13.5a4 4 0 0 0 5.66 0l2-2a4 4 0 1 0-5.66-5.66l-1 1M13.5 10.5a4 4 0 0 0-5.66 0l-2 2a4 4 0 1 0 5.66 5.66l1-1" />
);

export const getInsuranceLeakageRows = (t: Translate): NumberedStatRow[] => [
  {
    n: '01',
    title: t('Premium rated on a number that was already a year old'),
    body: (
      <>
        {t(
          'Turnover, headcount and asset values on a proposal form are recalled and rounded. Filed accounts carry the audited figure with a period end attached.',
        )}{' '}
        <b>{t('Rating against the filing closes the gap between the premium charged and the exposure taken.')}</b>
      </>
    ),
  },
  {
    n: '02',
    title: t('A trade description that no longer matches the filed activity'),
    body: (
      <>
        {t('The broker writes plain English. The register carries a classified code, dated at the point it changed.')}{' '}
        <b>
          {t('When a haulier quietly adds hazardous waste handling, the code moves months before the schedule does.')}
        </b>
      </>
    ),
  },
  {
    n: '03',
    title: t('Accumulation running silently across subsidiaries you never linked'),
    body: (
      <>
        {t('Four policies, four submissions, four broker spellings, one ultimate parent in another jurisdiction.')}{' '}
        <b>
          {t(
            'Ownership resolved from share registers and beneficial-ownership filings shows the aggregate before the loss does.',
          )}
        </b>
      </>
    ),
  },
  {
    n: '04',
    title: t('Delegated authority audited once a year, changed every month'),
    body: (
      <>
        {t(
          'Coverholders, MGAs and appointed representatives change directors, control and registered status between audits.',
        )}{' '}
        <b>
          {t('Watching the entity register makes the change visible when it is filed, not at the next audit cycle.')}
        </b>
      </>
    ),
  },
  {
    n: '05',
    title: t('Twelve months of policy period with no view of the insured'),
    body: (
      <>
        {t(
          'Between bind and renewal an insured can register charges, lose directors, restate accounts or enter an insolvency process.',
        )}{' '}
        <b>{t('Each is a dated filing, and each is a reason to look again before the renewal invitation goes out.')}</b>
      </>
    ),
  },
];

export const getInsuranceLeakageStats = (t: Translate): NumberedStatItem[] => [
  {
    tag: t('Matched submissions'),
    value: 31,
    suffix: '%',
    icon: LEAKAGE_GAUGE_ICON,
    body: (
      <>
        {t('carried a filed turnover')} <b>{t('more than 20% above the declared figure')}</b>,{' '}
        {t('with an audited period end already on the register.')}
      </>
    ),
  },
  {
    tag: t('Rating class'),
    value: 6,
    prefix: '1 in ',
    icon: LEAKAGE_TAG_ICON,
    body: (
      <>
        {t('had a filed activity code sitting in')} <b>{t('a different rating class')}</b>{' '}
        {t('from the trade description supplied by the broker.')}
      </>
    ),
  },
  {
    tag: t('Undisclosed group'),
    value: 22,
    suffix: '%',
    icon: LEAKAGE_LINK_ICON,
    body: (
      <>
        {t('sat under')} <b>{t('a parent or group link not disclosed')}</b> {t('anywhere on the submission.')}
      </>
    ),
  },
];

export const getInsuranceDeskItems = (t: Translate): NumberedFeatureCard[] => [
  {
    num: '01',
    tag: t('Underwriting'),
    title: t('Commercial underwriting'),
    description: t(
      'Verified legal entity, filed activity codes, turnover and headcount from accounts, group structure, registered charges and twelve years of filing history. Whether the submission is rated on the company that actually files.',
    ),
  },
  {
    num: '02',
    tag: t('Credit'),
    title: t('Trade credit and surety'),
    description: t(
      'Filed financial statements line by line, charge registers, insolvency events and the ownership chain sitting behind the buyer. The limit you set on a buyer, and how fast you pull it.',
    ),
  },
  {
    num: '03',
    tag: t('Distribution'),
    title: t('Delegated authority'),
    description: t(
      'Registered status, regulatory identifiers, directors and control, ownership changes and adverse filings across every entity holding a binder. Whether a binder holder still looks the way it did at approval.',
    ),
  },
  {
    num: '04',
    tag: t('Claims'),
    title: t('Claims and counter-fraud'),
    description: t(
      'Shared directors and addresses across entities, incorporation and dissolution patterns, links to suppliers and repairers, and the record as it stood at date of loss. Which claims sit inside a network rather than on their own.',
    ),
  },
  {
    num: '05',
    tag: t('Compliance'),
    title: t('Financial crime and sanctions'),
    description: t(
      'Holdings resolved to natural persons so aggregate ownership tests can be applied, group-wide screening, and a dated trail behind every match decision. Whether the risk can be written at all, and what you can evidence.',
    ),
  },
  {
    num: '06',
    tag: t('Portfolio'),
    title: t('Portfolio and reinsurance'),
    description: t(
      'Resolved corporate hierarchies across the whole book, daily-synced change feeds, and open identifiers you can join to your own policy administration data. What your true aggregate is before the cat model runs.',
    ),
  },
];

export const getAccessRoutes = (t: Translate): AccessRoute[] => [
  {
    name: t('Platform'),
    desc: t('Search, screen and export company records from the web application.'),
    actions: [{ label: t('Open the platform'), href: '/platform' }],
  },
  {
    name: 'API',
    desc: t(
      'Query any company by registry identifier. Documented endpoints, JSON responses, provenance on every field.',
    ),
    actions: [{ label: t('Read the API docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Bulk data feeds'),
    desc: t('Full datasets and scheduled deltas, delivered to your warehouse on your cadence.'),
    actions: [{ label: t('Request a feed'), href: '/book-a-demo' }],
  },
  {
    name: 'CRM',
    desc: t('Enrich and verify accounts inside Salesforce, HubSpot and Microsoft Dynamics 365.'),
    actions: [
      { label: 'Salesforce', href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
      { label: 'HubSpot', href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
    ],
  },
  {
    name: 'MCP',
    desc: t('Connect the dataset to any MCP-compatible agent or tool.'),
    actions: [{ label: 'MCP server', href: '/mcp-integration' }],
  },
  {
    name: t('Assistant connectors'),
    desc: t('Pull registry data straight into the assistant your teams already use.'),
    actions: [
      { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    name: 'Regis AI',
    desc: t('Ask questions in natural language across the dataset and get answers with their sources attached.'),
    actions: [{ label: t('Meet Regis AI'), href: '/regis' }],
  },
];
